import React, { useEffect, useMemo, useState } from 'react';
import AdminShell from '../../components/admin/AdminShell';
import LoadingDNA from '../../components/LoadingDNA';
import { EmptyState, KpiCard, MetaStrip, SectionCard, StatusBadge, TableControls } from '../../components/admin/AdminPrimitives';
import {
  formatCurrencyINRFull,
  formatDateIN,
  normalizeWorkflowStatus,
  workflowStatusLabel,
  workflowStatusTone,
} from '../../utils/adminFormat';
import {
  EmployeeDirectoryRecord,
  getCompanyEmployees,
  isApiError,
} from '../../services/dashboardService';
import { bulkDecisionExpenseSplits, getExpenseEvents, isExpenseApiError, remindManagersForSplits } from '../../services/expenseService';

type ExpenseLine = {
  id: string;
  employee_id: string;
  category?: string;
  merchant?: string;
  amount: number;
  status?: string;
  created_at?: string;
  trust_flagged?: boolean;
  trust_reasons?: string[];
};
const APPROVAL_SLA_BREACH_HOURS = 48;

const AdminExpensesPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [expenses, setExpenses] = useState<ExpenseLine[]>([]);
  const [employeeById, setEmployeeById] = useState<Map<string, EmployeeDirectoryRecord>>(new Map());
  const [selectedDepartment, setSelectedDepartment] = useState('All Departments');
  const [actionSearch, setActionSearch] = useState('');
  const [ledgerSearch, setLedgerSearch] = useState('');
  const [actionStatusFilter, setActionStatusFilter] = useState<'all' | 'pending' | 'rejected'>('all');
  const [ledgerStatusFilter, setLedgerStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected' | 'reimbursed' | 'draft'>('all');
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const [selectedActionIds, setSelectedActionIds] = useState<string[]>([]);
  const [isSubmittingBulk, setIsSubmittingBulk] = useState(false);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        setErrorMessage(null);
        setInfoMessage(null);
        const [events, employeeRows] = await Promise.all([getExpenseEvents({ perPage: 500 }), getCompanyEmployees()]);
        const expenseLines = events.flatMap((event) =>
          (event.splits || []).map((split) => ({
            id: split.id,
            employee_id: split.beneficiary_employee_id,
            category: event.category || 'Expense Share',
            merchant: event.merchant || '-',
            amount: Number(split.split_amount || 0),
            status: String(split.status || '').toLowerCase(),
            created_at: split.created_at || event.created_at,
            trust_flagged: Boolean(event.trust_signals?.flagged),
            trust_reasons: (event.trust_signals?.signals || []).map((signal) => signal.title),
          }))
        );
        setExpenses(expenseLines);
        setEmployeeById(new Map(employeeRows.map((row) => [row.id, row])));
        setLastRefreshedAt(new Date());
      } catch (error) {
        if (isExpenseApiError(error)) setErrorMessage(error.message);
        else if (isApiError(error)) setErrorMessage(error.message);
        else if (error instanceof Error) setErrorMessage(error.message);
        else setErrorMessage('Failed to load expenses.');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const departmentOptions = useMemo(() => {
    const values = new Set<string>();
    expenses.forEach((item) => {
      const employee = employeeById.get(item.employee_id);
      const department = employee?.department || 'No department';
      values.add(department);
    });
    return ['All Departments', ...Array.from(values).sort((a, b) => a.localeCompare(b))];
  }, [employeeById, expenses]);

  const filteredExpenses = useMemo(() => {
    if (selectedDepartment === 'All Departments') return expenses;
    return expenses.filter((item) => {
      const employee = employeeById.get(item.employee_id);
      const department = employee?.department || 'No department';
      return department === selectedDepartment;
    });
  }, [employeeById, expenses, selectedDepartment]);

  const totalAmount = useMemo(() => filteredExpenses.reduce((sum, item) => sum + Number(item.amount || 0), 0), [filteredExpenses]);
  const pendingCount = useMemo(
    () => filteredExpenses.filter((item) => normalizeWorkflowStatus(item.status) === 'pending').length,
    [filteredExpenses]
  );
  const rejectedCount = useMemo(
    () => filteredExpenses.filter((item) => normalizeWorkflowStatus(item.status) === 'rejected').length,
    [filteredExpenses]
  );

  const matchesExpenseSearch = (item: ExpenseLine, query: string): boolean => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    const employee = employeeById.get(item.employee_id);
    const employeeName = employee?.name || employee?.email || '';
    const managerName = employee?.manager_name || '';
    const department = employee?.department || 'No department';
    const haystack = [
      item.id,
      employeeName,
      managerName,
      department,
      item.category || '',
      item.merchant || '',
    ].join(' ').toLowerCase();
    return haystack.includes(q);
  };

  const actionBaseRows = useMemo(() => {
    return filteredExpenses.filter((item) => {
      const status = normalizeWorkflowStatus(item.status);
      return status === 'pending' || status === 'rejected';
    });
  }, [filteredExpenses]);

  const actionQueueRows = useMemo(() => {
    return actionBaseRows
      .filter((item) => {
        const status = normalizeWorkflowStatus(item.status);
        if (actionStatusFilter !== 'all' && status !== actionStatusFilter) return false;
        return matchesExpenseSearch(item, actionSearch);
      })
      .sort((a, b) => Number(b.amount || 0) - Number(a.amount || 0))
      .slice(0, 200);
  }, [actionBaseRows, actionSearch, actionStatusFilter]);

  const ledgerRows = useMemo(() => {
    return filteredExpenses.filter((item) => {
      const status = normalizeWorkflowStatus(item.status);
      if (ledgerStatusFilter !== 'all' && status !== ledgerStatusFilter) return false;
      return matchesExpenseSearch(item, ledgerSearch);
    });
  }, [filteredExpenses, ledgerSearch, ledgerStatusFilter]);

  const hasSectionData = filteredExpenses.length > 0;
  const hasActionBaseData = actionBaseRows.length > 0;
  const visibleActionIds = useMemo(() => actionQueueRows.map((item) => item.id), [actionQueueRows]);
  const selectablePendingIds = useMemo(
    () => actionQueueRows.filter((item) => normalizeWorkflowStatus(item.status) === 'pending').map((item) => item.id),
    [actionQueueRows],
  );
  const selectedPendingIds = useMemo(
    () => selectedActionIds.filter((id) => selectablePendingIds.includes(id)),
    [selectedActionIds, selectablePendingIds],
  );

  const toggleSelectAllActionRows = () => {
    if (visibleActionIds.length === 0) return;
    const allSelected = visibleActionIds.every((id) => selectedActionIds.includes(id));
    if (allSelected) {
      setSelectedActionIds((prev) => prev.filter((id) => !visibleActionIds.includes(id)));
      return;
    }
    setSelectedActionIds((prev) => Array.from(new Set([...prev, ...visibleActionIds])));
  };

  const toggleActionRowSelection = (id: string) => {
    setSelectedActionIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const runBulkAction = async (action: 'APPROVED' | 'REJECTED' | 'REMIND') => {
    try {
      setErrorMessage(null);
      setInfoMessage(null);
      setIsSubmittingBulk(true);
      if (action === 'REMIND') {
        const result = await remindManagersForSplits({ split_ids: selectedPendingIds });
        setInfoMessage(`Reminder sent for ${result.sent} split(s).`);
      } else {
        const result = await bulkDecisionExpenseSplits({ split_ids: selectedPendingIds, decision: action });
        if (result.updated > 0) {
          const targetStatus = action === 'APPROVED' ? 'approved' : 'rejected';
          setExpenses((prev) => prev.map((row) => (selectedPendingIds.includes(row.id) ? { ...row, status: targetStatus } : row)));
        }
        setInfoMessage(`${result.updated} updated, ${result.skipped} skipped.`);
      }
      setSelectedActionIds([]);
    } catch (error) {
      if (isExpenseApiError(error)) setErrorMessage(error.message);
      else if (error instanceof Error) setErrorMessage(error.message);
      else setErrorMessage('Bulk action failed.');
    } finally {
      setIsSubmittingBulk(false);
    }
  };

  const exportActionPack = () => {
    const header = ['split_id', 'employee', 'department', 'category', 'merchant', 'amount', 'status', 'sla_age_hours', 'created_at'];
    const escapeCell = (value: string | number | null | undefined): string => {
      const text = String(value ?? '');
      if (text.includes(',') || text.includes('"') || text.includes('\n')) {
        return `"${text.replace(/"/g, '""')}"`;
      }
      return text;
    };
    const rows = actionQueueRows.map((item) => {
      const employee = employeeById.get(item.employee_id);
      const createdAt = item.created_at ? new Date(item.created_at).getTime() : Date.now();
      const ageHours = Math.max(0, Math.floor((Date.now() - createdAt) / (1000 * 60 * 60)));
      return [
        item.id,
        employee?.name || employee?.email || 'Unknown employee',
        employee?.department || 'No department',
        item.category || '-',
        item.merchant || '-',
        Number(item.amount || 0),
        normalizeWorkflowStatus(item.status),
        ageHours,
        item.created_at || '',
      ];
    });
    const csv = [header.join(','), ...rows.map((row) => row.map(escapeCell).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.setAttribute('download', `expense-action-queue-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  };

  return (
    <AdminShell
      pageTitle="Expenses"
      subtitle="Track and control company-wide expense records."
    >
      {isLoading ? <LoadingDNA /> : null}
      {errorMessage ? <p className="rounded-lg bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{errorMessage}</p> : null}
      {infoMessage ? <p className="rounded-lg bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">{infoMessage}</p> : null}

      {!isLoading ? (
        <div className="space-y-4">
          <MetaStrip
            left={`Reporting period: current workflow cycle${selectedDepartment !== 'All Departments' ? ` | Department: ${selectedDepartment}` : ''}`}
            right={`Last refreshed: ${lastRefreshedAt ? lastRefreshedAt.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}`}
          />

          <div className="grid gap-3 md:grid-cols-3">
            <KpiCard label="Total Spend" value={formatCurrencyINRFull(totalAmount)} helper={`${filteredExpenses.length} claims in current cycle`} />
            <KpiCard label="Claims Awaiting Approval" value={String(pendingCount)} helper="Manager/finance action needed" tone={pendingCount > 0 ? 'warning' : 'positive'} />
            <KpiCard label="Rejected Claims" value={String(rejectedCount)} helper="Current cycle | requires employee rework" tone={rejectedCount > 0 ? 'danger' : 'positive'} />
          </div>

          <SectionCard
            title="Exceptions Requiring Action"
            subtitle="Pending and rejected claims sorted by amount."
            actions={
              hasSectionData ? (
                <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Department
                  <select
                    value={selectedDepartment}
                    onChange={(event) => setSelectedDepartment(event.target.value)}
                    className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm font-semibold text-slate-700 outline-none focus:border-slate-500"
                  >
                    {departmentOptions.map((department) => (
                      <option key={department} value={department}>
                        {department}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null
            }
          >
            {hasActionBaseData ? (
              <>
                <TableControls
                  searchValue={actionSearch}
                  onSearchChange={setActionSearch}
                  searchPlaceholder="Search expense ID, employee, manager, category, merchant..."
                  filters={(
                    <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Status
                      <select
                        value={actionStatusFilter}
                        onChange={(event) => setActionStatusFilter(event.target.value as 'all' | 'pending' | 'rejected')}
                        className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm font-semibold text-slate-700 outline-none focus:border-slate-500"
                      >
                        <option value="all">All</option>
                        <option value="pending">Pending</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </label>
                  )}
                />
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={exportActionPack}
                    className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Bulk Export
                  </button>
                  <div className="flex flex-wrap items-center gap-2">
                    {selectedActionIds.length > 0 ? <span className="text-xs font-semibold text-slate-600">{selectedActionIds.length} selected</span> : null}
                    {selectedPendingIds.length > 0 ? (
                      <button type="button" disabled={isSubmittingBulk} onClick={() => runBulkAction('APPROVED')} className="rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white disabled:opacity-60">
                        Bulk Approve
                      </button>
                    ) : null}
                    {selectedPendingIds.length > 0 ? (
                      <button type="button" disabled={isSubmittingBulk} onClick={() => runBulkAction('REJECTED')} className="rounded-full bg-red-600 px-3 py-1 text-xs font-semibold text-white disabled:opacity-60">
                        Bulk Reject
                      </button>
                    ) : null}
                    {selectedPendingIds.length > 0 ? (
                      <button type="button" disabled={isSubmittingBulk} onClick={() => runBulkAction('REMIND')} className="rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800 disabled:opacity-60">
                        Bulk Remind
                      </button>
                    ) : null}
                  </div>
                </div>

                {actionQueueRows.length === 0 ? (
                  <EmptyState compact message="No pending or rejected claims match the selected filters." />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[1020px]">
                      <thead>
                        <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                          <th className="pb-2">
                            <input
                              type="checkbox"
                              checked={visibleActionIds.length > 0 && visibleActionIds.every((id) => selectedActionIds.includes(id))}
                              onChange={toggleSelectAllActionRows}
                            />
                          </th>
                          <th className="pb-2">Expense ID</th>
                          <th className="pb-2">Employee</th>
                          <th className="pb-2">Department</th>
                          <th className="pb-2">Category</th>
                          <th className="pb-2 text-right">Amount</th>
                          <th className="pb-2">Status</th>
                          <th className="pb-2">Trust</th>
                          <th className="pb-2">SLA Age</th>
                          <th className="pb-2">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {actionQueueRows.map((item) => {
                          const employee = employeeById.get(item.employee_id);
                          const employeeName = employee?.name || employee?.email || 'Unknown employee';
                          const department = employee?.department || 'No department';
                          const status = normalizeWorkflowStatus(item.status);
                          const createdAt = item.created_at ? new Date(item.created_at).getTime() : Date.now();
                          const ageHours = Math.max(0, Math.floor((Date.now() - createdAt) / (1000 * 60 * 60)));
                          const isBreach = status === 'pending' && ageHours >= APPROVAL_SLA_BREACH_HOURS;

                          return (
                            <tr key={item.id} className="border-b border-slate-100 text-sm text-slate-700">
                              <td className="py-3">
                                <input
                                  type="checkbox"
                                  checked={selectedActionIds.includes(item.id)}
                                  onChange={() => toggleActionRowSelection(item.id)}
                                />
                              </td>
                              <td className="py-3 font-semibold text-slate-900">{item.id.slice(0, 8)}</td>
                              <td className="py-3">{employeeName}</td>
                              <td className="py-3">{department}</td>
                              <td className="py-3">{item.category || '-'}</td>
                              <td className="py-3 text-right font-semibold text-slate-900">{formatCurrencyINRFull(Number(item.amount || 0))}</td>
                              <td className="py-3"><StatusBadge label={workflowStatusLabel(status)} tone={workflowStatusTone(status)} /></td>
                              <td className="py-3">
                                {item.trust_flagged ? (
                                  <span title={(item.trust_reasons || []).join(' | ')}><StatusBadge label="Flagged" tone="danger" /></span>
                                ) : (
                                  <StatusBadge label="Clean" tone="positive" />
                                )}
                              </td>
                              <td className="py-3">
                                <div className="inline-flex items-center gap-2">
                                  <span>{ageHours}h</span>
                                  {isBreach ? <StatusBadge label="Breach" tone="danger" /> : null}
                                </div>
                              </td>
                              <td className="py-3">{formatDateIN(item.created_at)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm font-semibold text-slate-600">No pending or rejected claims for this selection.</p>
            )}
          </SectionCard>

          <SectionCard title="Expense Ledger" subtitle="Full expense lines for audit and reconciliation.">
            {hasSectionData ? (
              <>
                <TableControls
                  searchValue={ledgerSearch}
                  onSearchChange={setLedgerSearch}
                  searchPlaceholder="Search all ledger rows..."
                  filters={(
                    <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Status
                      <select
                        value={ledgerStatusFilter}
                        onChange={(event) => setLedgerStatusFilter(event.target.value as 'all' | 'pending' | 'approved' | 'rejected' | 'reimbursed' | 'draft')}
                        className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm font-semibold text-slate-700 outline-none focus:border-slate-500"
                      >
                        <option value="all">All</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="reimbursed">Reimbursed</option>
                        <option value="draft">Draft</option>
                      </select>
                    </label>
                  )}
                />

                {ledgerRows.length === 0 ? (
                  <EmptyState compact message="No expense rows match the selected search and filters." />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[1180px]">
                      <thead>
                        <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                          <th className="pb-2">Expense ID</th>
                          <th className="pb-2">Employee</th>
                          <th className="pb-2">Manager</th>
                          <th className="pb-2">Department</th>
                          <th className="pb-2">Category</th>
                          <th className="pb-2">Merchant</th>
                          <th className="pb-2 text-right">Amount</th>
                          <th className="pb-2">Status</th>
                          <th className="pb-2">Trust</th>
                          <th className="pb-2">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ledgerRows.map((item) => {
                          const employee = employeeById.get(item.employee_id);
                          const employeeName = employee?.name || employee?.email || 'Unknown employee';
                          const managerName = employee?.manager_name || 'Unassigned';
                          const department = employee?.department || 'No department';
                          const status = normalizeWorkflowStatus(item.status);

                          return (
                            <tr key={item.id} className="border-b border-slate-100 text-sm text-slate-700">
                              <td className="py-3 font-semibold text-slate-900">{item.id.slice(0, 8)}</td>
                              <td className="py-3">{employeeName}</td>
                              <td className="py-3">{managerName}</td>
                              <td className="py-3">{department}</td>
                              <td className="py-3">{item.category || '-'}</td>
                              <td className="py-3">{item.merchant || '-'}</td>
                              <td className="py-3 text-right font-semibold text-slate-900">{formatCurrencyINRFull(Number(item.amount || 0))}</td>
                              <td className="py-3"><StatusBadge label={workflowStatusLabel(status)} tone={workflowStatusTone(status)} /></td>
                              <td className="py-3">
                                {item.trust_flagged ? (
                                  <span title={(item.trust_reasons || []).join(' | ')}><StatusBadge label="Flagged" tone="danger" /></span>
                                ) : (
                                  <StatusBadge label="Clean" tone="positive" />
                                )}
                              </td>
                              <td className="py-3">{formatDateIN(item.created_at)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm font-semibold text-slate-600">No expenses available for this selection.</p>
            )}
          </SectionCard>
        </div>
      ) : null}
    </AdminShell>
  );
};

export default AdminExpensesPage;
